import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import {
  Building2,
  UserPlus,
  Upload,
  Target,
  FileUp,
  ScanSearch,
  MessageCircleQuestion,
  Scale,
  Trophy,
  Sparkles,
  PenLine,
  FileDown,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronRight,
  X,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useOnboardingStore } from "@/stores/onboarding-store"
import { useAuthStore } from "@/stores/auth-store"
import { cn } from "@/lib/utils"

// ===================
// Step Configuration
// ===================

export interface OnboardingStep {
  key: string
  title: string
  description: string
  icon: LucideIcon
  link: string
  helpSlug: string
  adminOnly?: boolean
}

export interface OnboardingPhase {
  key: string
  title: string
  steps: OnboardingStep[]
}

export const ONBOARDING_PHASES: OnboardingPhase[] = [
  {
    key: "setup",
    title: "Set up",
    steps: [
      {
        key: "company_profile",
        title: "Complete your company profile",
        description: "Add services, certifications, and capabilities",
        icon: Building2,
        link: "/app/knowledge/company-profile",
        helpSlug: "managing-your-organisation",
        adminOnly: true,
      },
      {
        key: "invite_team",
        title: "Invite your team",
        description: "Bring in the people who'll write and review with you",
        icon: UserPlus,
        link: "/app/settings/users",
        helpSlug: "working-with-your-team",
        adminOnly: true,
      },
      {
        key: "upload_library",
        title: "Upload documents to your library",
        description: "Past proposals, case studies, and company knowledge",
        icon: Upload,
        link: "/app/knowledge/library",
        helpSlug: "building-your-content-library",
      },
    ],
  },
  {
    key: "first_bid",
    title: "Your first bid",
    steps: [
      {
        key: "create_bid",
        title: "Create your first bid",
        description: "Set up a tender or RFP you are responding to",
        icon: Target,
        link: "/app/bids",
        helpSlug: "creating-your-first-bid",
      },
      {
        key: "upload_tender",
        title: "Upload tender documents",
        description: "Add RFP documents to a bid for AI analysis",
        icon: FileUp,
        link: "/app/bids",
        helpSlug: "analysing-a-tender",
      },
      {
        key: "review_analysis",
        title: "Review your tender analysis",
        description: "See the AI-extracted summary, requirements, and compliance matrix",
        icon: ScanSearch,
        link: "/app/bids",
        helpSlug: "analysing-a-tender",
      },
      {
        key: "ask_ai",
        title: "Ask the AI a question",
        description: "Chat with your tender documents — answers come with citations",
        icon: MessageCircleQuestion,
        link: "/app/bids",
        helpSlug: "asking-the-ai-questions",
      },
      {
        key: "go_no_go",
        title: "Decide go or no-go",
        description: "Run a structured assessment before you commit",
        icon: Scale,
        link: "/app/bids",
        helpSlug: "deciding-go-or-no-go",
      },
      {
        key: "win_themes",
        title: "Define your win themes",
        description: "Capture the arguments your response should keep making",
        icon: Trophy,
        link: "/app/bids",
        helpSlug: "win-themes-and-strategy",
      },
    ],
  },
  {
    key: "deliver",
    title: "Draft & deliver",
    steps: [
      {
        key: "generate_plan",
        title: "Propose a response structure",
        description: "Use AI to create a structured outline",
        icon: Sparkles,
        link: "/app/bids",
        helpSlug: "proposing-a-response-structure",
      },
      {
        key: "draft_section",
        title: "Draft a section with AI",
        description: "Let AI write with evidence and citations",
        icon: PenLine,
        link: "/app/bids",
        helpSlug: "drafting-with-ai",
      },
      {
        key: "export_response",
        title: "Export your response",
        description: "Produce a submission-ready document",
        icon: FileDown,
        link: "/app/bids",
        helpSlug: "exporting-your-response",
      },
    ],
  },
]

export const ONBOARDING_STEPS: OnboardingStep[] = ONBOARDING_PHASES.flatMap((p) => p.steps)

// ===================
// Component
// ===================

export function OnboardingChecklist() {
  const { steps, dismiss } = useOnboardingStore()
  const role = useAuthStore((s) => s.role)
  const isAdmin = role === "admin"

  const phases = useMemo(
    () =>
      ONBOARDING_PHASES.map((phase) => ({
        ...phase,
        steps: phase.steps.filter((s) => !s.adminOnly || isAdmin),
      })).filter((phase) => phase.steps.length > 0),
    [isAdmin]
  )

  const visibleSteps = useMemo(() => phases.flatMap((p) => p.steps), [phases])
  const completedCount = visibleSteps.filter((s) => steps[s.key]).length
  const totalSteps = visibleSteps.length

  // The first phase that still has pending steps is the one worth showing.
  const defaultExpandedKey =
    phases.find((phase) => !phase.steps.every((s) => steps[s.key]))?.key ?? null

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(phases.map((p) => [p.key, p.key === defaultExpandedKey]))
  )

  // `steps` starts empty and arrives async, so the first computed default is a
  // guess. Recompute it when the fetched status lands — but never override a
  // choice the user has already made.
  const userToggledRef = useRef(false)
  useEffect(() => {
    if (userToggledRef.current) return
    setExpanded(Object.fromEntries(phases.map((p) => [p.key, p.key === defaultExpandedKey])))
  }, [defaultExpandedKey, phases])

  const togglePhase = (key: string) => {
    userToggledRef.current = true
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const nextStep = visibleSteps.find((s) => !steps[s.key])
  // Fold to one row once the user is at least halfway through, so a nearly
  // finished checklist stops dominating Home. Below half it stays in full.
  const isCompact = nextStep !== undefined && completedCount * 2 >= totalSteps
  const [showAllSteps, setShowAllSteps] = useState(false)

  const dismissButton = (
    <button
      onClick={() => void dismiss()}
      className="rounded-md p-1 text-subtle hover:text-muted-foreground hover:bg-muted transition-colors"
      aria-label="Dismiss getting started checklist"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  )

  const phaseList = (
    <div className="divide-y divide-muted/50">
      {phases.map((phase) => {
        const doneCount = phase.steps.filter((s) => steps[s.key]).length
        const isComplete = doneCount === phase.steps.length
        const isExpanded = expanded[phase.key] ?? false

        return (
          <div key={phase.key}>
            <button
              type="button"
              onClick={() => togglePhase(phase.key)}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/30"
              aria-expanded={isExpanded}
              aria-controls={`onboarding-phase-${phase.key}`}
            >
              {isComplete ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(var(--status-success))]" />
              ) : (
                <span className="h-4 w-4 shrink-0" />
              )}
              <span className="flex-1 text-[13px] font-medium">{phase.title}</span>
              <span className="text-meta">
                {doneCount}/{phase.steps.length}
              </span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                  !isExpanded && "-rotate-90"
                )}
              />
            </button>

            {isExpanded && (
              <div
                id={`onboarding-phase-${phase.key}`}
                className="divide-y divide-muted/50 border-t border-muted/50"
              >
                {phase.steps.map((step) => (
                  <StepRow key={step.key} step={step} isComplete={steps[step.key] ?? false} />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  if (isCompact && nextStep) {
    return (
      <section data-testid="onboarding-compact">
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-2.5">
            <ProgressRing done={completedCount} total={totalSteps} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px]">
                <span className="font-medium">Getting started</span>
                <span className="text-muted-foreground">
                  {" "}· {completedCount} of {totalSteps} done
                </span>
              </p>
              <p className="truncate text-meta mt-0.5" data-testid="onboarding-next-step">
                Next: <span className="text-foreground">{nextStep.title}</span> · {nextStep.description}
              </p>
            </div>
            <Link
              to={nextStep.link}
              className="inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[13px] text-[hsl(var(--link))] hover:underline"
            >
              Continue
              <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </Link>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowAllSteps((v) => !v)}
              aria-expanded={showAllSteps}
              aria-controls="onboarding-all-steps"
              aria-label={showAllSteps ? "Hide all steps" : "Show all steps"}
              className="h-7 w-7 shrink-0 text-subtle hover:text-muted-foreground"
            >
              <ChevronDown
                className={cn("h-4 w-4 transition-transform", showAllSteps && "rotate-180")}
              />
            </Button>
            {dismissButton}
          </div>
          {showAllSteps && (
            <div id="onboarding-all-steps" className="border-t border-muted/50">
              {phaseList}
            </div>
          )}
        </div>
      </section>
    )
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-2.5">
        <h2 className="text-[13px] font-medium">Getting started</h2>
        <div className="flex items-center gap-2">
          <span className="text-meta">
            {completedCount} of {totalSteps} complete
          </span>
          {dismissButton}
        </div>
      </div>

      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">{phaseList}</div>
    </section>
  )
}

// ===================
// Progress Ring
// ===================

const RING_RADIUS = 10
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

function ProgressRing({ done, total }: { done: number; total: number }) {
  const fraction = total > 0 ? done / total : 0
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7 shrink-0 -rotate-90" aria-hidden="true">
      <circle cx="12" cy="12" r={RING_RADIUS} fill="none" strokeWidth="3" className="stroke-muted" />
      <circle
        cx="12"
        cy="12"
        r={RING_RADIUS}
        fill="none"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={RING_CIRCUMFERENCE * (1 - fraction)}
        className="stroke-[hsl(var(--status-success))]"
      />
    </svg>
  )
}

// ===================
// Step Row
// ===================

function StepRow({ step, isComplete }: { step: OnboardingStep; isComplete: boolean }) {
  const Icon = step.icon

  return (
    <div className="group flex items-center gap-3 py-2.5 pl-8 pr-3 transition-colors hover:bg-muted/30">
      <Link to={step.link} className="flex min-w-0 flex-1 items-center gap-3">
        {isComplete ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(var(--status-success))]" />
        ) : (
          <Circle className="h-4 w-4 shrink-0 text-muted-foreground/40" />
        )}

        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />

        <div className="min-w-0 flex-1">
          <p className={cn("text-[13px]", isComplete && "text-muted-foreground")}>{step.title}</p>
          <p className="text-[11px] text-muted-foreground">{step.description}</p>
        </div>

        {/* Inside the Link so the hover affordance and the clickable region are
            the same element. The Learn link stays a sibling — anchors must not nest. */}
        {!isComplete && (
          <ChevronRight
            aria-hidden="true"
            className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
          />
        )}
      </Link>

      {isComplete ? (
        <span className="shrink-0 text-meta">Done</span>
      ) : (
        <Link
          to={`/app/help/${step.helpSlug}`}
          aria-label={`Learn about ${step.title}`}
          className="shrink-0 text-meta hover:text-foreground"
        >
          Learn
        </Link>
      )}
    </div>
  )
}
